import { useStammdatenRepository } from '@Hooks/useRepository';
import { isCompletePostalCode, type ApiPostalCodeLookup, type EnumCountry } from '@Model';
import { PostalCodesRepository } from '@Repositories/Stammdaten';
import { unwrapOr, useDebouncedValue } from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

// @ai-generated 2026-09-14T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

/**
 * What the locality directory has to say about a postal code. `idle` means there is nothing to say —
 * the postal code is incomplete, or the country keeps no locality data. `unavailable` says nothing
 * about the postal code either: it reports an outage on our side.
 */
export type LocalityLookupStatus = 'idle' | 'searching' | 'unavailable' | 'unknown' | 'ready';

/** The directory's answer, always about the country and postal code it names — never a previous one. */
export interface LocalityLookup {
    country: EnumCountry;
    zip: string;
    status: LocalityLookupStatus;
    /** Locality names for `zip`. Empty unless the status is `ready`. */
    localities: string[];
}

/** Lookup not available — says nothing about the postal code. */
const UNAVAILABLE: ApiPostalCodeLookup = { localities: [], is_available: false };

/**
 * How long a keystroke waits before it becomes a question. Short enough to feel immediate while the
 * postal code is being typed, long enough that the digits on the way to it never reach the provider.
 */
const LOOKUP_DEBOUNCE_MS = 100;

/**
 * How long an answer is reused on the client. Finite, not `Infinity`: locality data rarely changes,
 * but a session runs for a long time, and the server caches anyway.
 */
const LOOKUP_STALE_TIME_MS = 30 * 60 * 1000;

function lookupOf(country: EnumCountry, zip: string, status: LocalityLookupStatus): LocalityLookup {
    return { country, zip, status, localities: [] };
}

function toLookup(response: ApiPostalCodeLookup, country: EnumCountry, zip: string): LocalityLookup {
    if (!response.is_available) {
        // An outage of the locality database is no reason to claim this postal code has no locality.
        return lookupOf(country, zip, 'unavailable');
    }

    const localities = response.localities.map((locality) => locality.city);
    return { country, zip, status: localities.length === 0 ? 'unknown' : 'ready', localities };
}

/**
 * The localities registered for a postal code (`/postal_codes:query`). Pure convenience: address
 * entry is never blocked, so an unknown country, an incomplete postal code and an outage all end in
 * a state that reports itself rather than in an error.
 *
 * Follows the postal code instead of being triggered by it — pass what the field shows and the
 * answer follows on its own, no more often than the debounce allows. The answer always names the
 * postal code it belongs to, so a caller can never mistake a stale answer for a current one.
 *
 * `enabled` is what a caller that may not use the answer switches off. It reports `idle`, the same
 * state an incomplete postal code ends in, so everything downstream falls silent by itself.
 */
export function useLocalitiesForPostalCode(country: EnumCountry, zip: string, enabled = true): LocalityLookup {
    const postalCodes = useStammdatenRepository(PostalCodesRepository);

    const askedFor = zip.trim();
    const settled = useDebouncedValue(askedFor, LOOKUP_DEBOUNCE_MS);

    const { data, isError } = useQuery({
        queryKey: ['stammdaten', 'plz-lookup', country, settled],
        enabled: enabled && isCompletePostalCode(country, settled),
        // An outage is a statement about us, not about the postal code, and must not freeze in the
        // cache: this postal code would stay unresolvable for the rest of the session.
        staleTime: (query) => (query.state.data?.is_available === true ? LOOKUP_STALE_TIME_MS : 0),
        queryFn: async () => {
            return unwrapOr(await postalCodes.lookup(country, settled), UNAVAILABLE);
        },
    });

    return useMemo(() => {
        if (!enabled || !isCompletePostalCode(country, askedFor)) {
            return lookupOf(country, askedFor, 'idle');
        }
        // While the debounce is still running, the answer at hand belongs to the postal code before.
        if (settled !== askedFor) {
            return lookupOf(country, askedFor, 'searching');
        }
        if (data !== undefined) {
            // An answer at hand outlives a failed refetch: it is still what the directory said.
            return toLookup(data, country, askedFor);
        }
        return lookupOf(country, askedFor, isError ? 'unavailable' : 'searching');
    }, [enabled, country, askedFor, settled, data, isError]);
}
